import { Button, HStack, useToast } from "@chakra-ui/react";
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SubmissionForm, {
  QuizSubmissionHandle,
} from "../../components/forms/QuizSubmissionForm";
import useCreateQuizSubmission from "../../hooks/api/quiz/useCreateQuizSubmission";
import useGetQuizDetails from "../../hooks/api/quiz/useGetQuizDetails";
import useGetQuizSubmissionDetails from "../../hooks/api/quiz/useGetQuizSubmissionDetails";
import useAuthState from "../../state/auth.state";
import BasePage from "./BasePage";

type Params = {
  quizId: string;
};

type LocationState = {
  submissionId?: number;
};

export default function QuizSubmissionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { quizId } = useParams<Params>();
  const { role } = useAuthState();
  const formRef = useRef<QuizSubmissionHandle>(null);
  const toast = useToast();
  const {
    state: { submissionId },
  } = useLocation() as { state: LocationState };

  const {
    data: quiz,
    isLoading: isQuizLoading,
    isError: isQuizError,
    refetch: refetchQuiz,
  } = useGetQuizDetails(quizId ?? "");

  const {
    data: submission,
    isLoading: isSubmissionLoading,
    isError: isSubmissionError,
    refetch: refetchSubmission,
  } = useGetQuizSubmissionDetails(parseInt(quizId ?? "0"), submissionId);

  const { mutateAsync: submit, isPending: isSubmitPending } =
    useCreateQuizSubmission(quizId ?? "");

  const refetchAll = useCallback(() => {
    return Promise.all([refetchSubmission, refetchQuiz]);
  }, [refetchSubmission, refetchQuiz]);

  const onSuccess = useCallback(() => {
    navigate("./../..");
    toast({
      title: t("quiz_submission_page.quiz_submitted"),
      description: t("quiz_submission_page.quiz_submitted_message"),
      status: "success",
      isClosable: true,
    });
  }, [navigate, t, toast]);

  const onError = useCallback(() => {
    toast({
      title: t("quiz_submission_page.quiz_error"),
      description: t("quiz_submission_page.quiz_error_message"),
      status: "error",
      isClosable: true,
    });
  }, [t, toast]);

  const userActions = useMemo(() => {
    let actions: JSX.Element | undefined = undefined;

    if (role === "ROLE_STUDENT" && !submission) {
      actions = (
        <HStack gap={4} p={4}>
          <Button
            bg="primary.DEFAULT"
            color="white"
            onClick={() =>
              formRef.current?.submit((submission) => {
                submit({ submission }).then(onSuccess).catch(onError);
              })
            }
            isLoading={isSubmitPending}
          >
            {t("common.buttons.submit")}
          </Button>
        </HStack>
      );
    }

    return actions;
  }, [isSubmitPending, onError, onSuccess, role, submission, submit, t]);

  return (
    <BasePage
      bottomRightContent={userActions}
      isLoading={isSubmissionLoading || isQuizLoading}
      hasError={isSubmissionError || isQuizError}
      retry={refetchAll}
      withBreadcrumb={false}
      title={quiz?.title}
      description={t(
        quiz?.type === "TASK"
          ? "quiz_submission_page.task"
          : "quiz_submission_page.questionnaire",
      )}
    >
      <SubmissionForm
        ref={formRef}
        quiz={quiz!}
        submission={submission}
        isDisabled={!!submission}
      />
    </BasePage>
  );
}
